import { useQuery } from '@tanstack/react-query';

import { tokenRequests } from '../api/api';
import cacheQueryKeys from '../resources/queryCacheKeys';
import { useToastController } from '../toast';
import { AvailableServicesResponse, CategoryType, IndustryType, PricingFormType } from '../types/types';
import { statusCodesMapper } from '../utils/exception';

const sortByOrder = (prevElement: IndustryType, nextElement: IndustryType) =>
  prevElement.orderIndex - nextElement.orderIndex;

const assignServicesToCategory =
  (data: AvailableServicesResponse) =>
  (category: CategoryType): Required<CategoryType> => {
    const services = data.services.filter((service) => service.categoryUuid === category.uuid).sort(sortByOrder);

    const subcategories = data.subcategories
      .filter((subcategory) => subcategory.categoryUuid === category.uuid)
      .sort(sortByOrder)
      .map((subcategory) => {
        const servicesForSubCategory = data.services
          .filter((service) => service.categoryUuid === subcategory.uuid)
          .sort(sortByOrder);
        return {
          ...subcategory,
          services: servicesForSubCategory,
        };
      });
    return {
      ...category,
      services,
      subcategories,
    };
  };

export type FormattedServicesData = {
  industries: {
    categories: Required<CategoryType>[];
    type: string;
    name: string;
    uuid: string;
    orderIndex: number;
  }[];
  pricingForms: PricingFormType[];
};

export const formatServicesData = (data: AvailableServicesResponse | null) => {
  if (!data) {
    return { industries: [], pricingForms: [] };
  }
  const sortedIndustries = data.industries.sort(sortByOrder);

  return {
    industries: sortedIndustries.map((industry) => {
      const categories = data.categories
        .filter((category) => category.industryUuid === industry.uuid)
        .sort(sortByOrder)
        .map(assignServicesToCategory(data));
      return {
        ...industry,
        categories,
      };
    }),
    pricingForms: data.priceForms,
  };
};

type Params = {
  token: string;
  onSuccess?: (data: FormattedServicesData) => void;
};

export const useServices = ({ token, onSuccess }: Params) => {
  const { showToast } = useToastController();

  return useQuery(
    [cacheQueryKeys.AvailablePublicServices],
    async () => {
      const response = await tokenRequests.fetchServices(token);

      return statusCodesMapper(response, {
        unhandled: () => {
          showToast('Something went wrong, please try again.');
          throw new Error();
        },
        success: (data) => {
          return formatServicesData(data);
        },
      });
    },
    {
      onSuccess: (data) => onSuccess && onSuccess(data as FormattedServicesData),
      staleTime: Infinity,
      refetchInterval: 1000 * 60 * 60, // Refetch every 60 minutes
    }
  );
};
